import { FC, useMemo, useState } from "react";
import { FileText } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/_atoms/dialog";
import { Label } from "@/components/_atoms/label";
import { RadioGroup, RadioGroupItem } from "@/components/_atoms/radio-group";
import { getColumns } from "@/domain/_shared/file/components/file-datatable/columns";
import { DataTable as FileDataTable } from "@/domain/_shared/file/components/file-datatable/datatable";
import { FileUploadAction, FileUploader, UploadResult } from "@/domain/_shared/file/components/file-uploader";
import { InputFile } from "@/domain/_shared/file/components/input-file";
import { OwnerType } from "@/domain/_shared/lib/owner-types";
import { MyFile } from "@/graphql/generated/graphql";

interface DocumentsSectionProps {
  ownerId: string;
  ownerType: OwnerType;
  isEditing: boolean;
  fileList: MyFile[];
  isLoading?: boolean;
}

export const DocumentsSection: FC<DocumentsSectionProps> = ({
  ownerId,
  ownerType,
  isEditing,
  fileList,
  isLoading = false,
}: DocumentsSectionProps) => {
  const { t } = useTranslation("common");
  const [addFileDialogOpen, setAddFileDialogOpen] = useState<boolean>(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState<boolean>(false);
  const [keepBoth, setKeepBoth] = useState<boolean>(false);
  const [startUpload, setStartUpload] = useState<boolean>(false);

  const [newFileList, setNewFileList] = useState<File[]>([]);
  const [uploadQeueFileList, setUploadQeueFileList] = useState<FileUploadAction[]>([]);

  const handleFilesAdded = (newFileList: File[]) => {
    setNewFileList(newFileList);

    if (newFileList.length === 0) {
      return;
    }

    const existingFiles = new Map(fileList.map((file) => [file.name, file]));
    const fileUploadActionList: FileUploadAction[] = [];
    let hasDuplicate = false;

    for (const newFile of newFileList) {
      const oldFile = existingFiles.get(newFile.name);

      if (oldFile) {
        hasDuplicate = true;
      }

      fileUploadActionList.push({
        id: crypto.randomUUID(),
        newFile,
        oldFileId: oldFile ? oldFile.id : undefined,
        conflictHandling: keepBoth ? "CREATE" : "REPLACE",
      });
    }

    if (!hasDuplicate) {
      setStartUpload(true);
      setUploadQeueFileList(fileUploadActionList);
    } else {
      setUploadQeueFileList([]);
      setDuplicateDialogOpen(true);
    }
  };

  function handleUploadOptionSelect() {
    const existingFiles = new Map(fileList.map((file) => [file.name, file]));
    const fileUploadActionList: FileUploadAction[] = [];
    for (const newFile of newFileList) {
      const oldFile = existingFiles.get(newFile.name);
      fileUploadActionList.push({
        id: crypto.randomUUID(),
        newFile: newFile,
        oldFileId: oldFile ? oldFile.id : undefined,
        conflictHandling: keepBoth ? "CREATE" : "REPLACE",
      });
    }

    setUploadQeueFileList(fileUploadActionList);
    setStartUpload(true);
    setDuplicateDialogOpen(false);
  }

  function handleFileUploadFinished(_result: UploadResult) {}

  const columns = useMemo(() => {
    return getColumns({ isReadOnly: !isEditing });
  }, [isEditing]);

  return (
    <>
      <InputFile
        fileList={newFileList}
        onChange={handleFilesAdded}
        open={addFileDialogOpen}
        onOpenChange={setAddFileDialogOpen}
      >
        <FileDataTable
          columns={columns}
          data={fileList}
          defaultSort={[{ id: "name", desc: false }]}
          isLoading={isLoading}
          emptyState={{
            icon: <FileText className="h-6 w-6" aria-hidden="true" />,
            title: t("fileDatatable.emptyTitle"),
            description: t("fileDatatable.emptyDescription"),
            ...(isEditing
              ? {
                  actionLabel: t("documents.upload"),
                  onAction: () => setAddFileDialogOpen(true),
                }
              : {}),
          }}
        >
          {isEditing && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => {
                setAddFileDialogOpen(true);
              }}
              aria-label={t("documents.uploadDocuments")}
            >
              {t("documents.upload")}
            </Button>
          )}
        </FileDataTable>
      </InputFile>

      {startUpload && (
        <FileUploader
          ownerId={ownerId}
          ownerType={ownerType}
          fileUploadList={uploadQeueFileList}
          onFileUploadFinished={handleFileUploadFinished}
        />
      )}

      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent>
          <DialogHeader className="sr-only">
            <DialogTitle className="sr-only">{t("documents.upload")}</DialogTitle>
            <DialogDescription className="sr-only">
              {t("documents.duplicateDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className=" sm:max-w-lg">
            <div>
              <span className="text-base font-normal">{t("documents.uploadOptions")} </span>
            </div>
            <div className="relative max-w-full font-light">
              <p className="mb-4 mt-4 text-xs">
                {t("documents.duplicateQuestion")}
              </p>
              <div>
                <RadioGroup
                  className=" space-y-2"
                  defaultValue={keepBoth ? "keepBoth" : "replace"}
                  onValueChange={(selectedValue: string) => {
                    if (selectedValue === "keepBoth") {
                      setKeepBoth(true);
                    } else {
                      setKeepBoth(false);
                    }
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="replace" id="r1" />
                    <Label htmlFor="r1">{t("documents.replaceExisting")}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="keepBoth" id="r2" />
                    <Label htmlFor="r2">{t("documents.keepAll")}</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
            <div className="mt-4 flex flex-row flex-wrap">
              <div className="flex w-full flex-row items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    setNewFileList([]);
                    setDuplicateDialogOpen(false);
                  }}
                  aria-label={t("documents.cancelUpload")}
                >
                  {t("actions.cancel")}
                </Button>
                <Button
                  type="button"
                  variant="default"
                  size="xs"
                  onClick={handleUploadOptionSelect}
                  aria-label={t("documents.uploadDocuments")}
                >
                  {t("actions.upload")}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
